import { ReactComponent as ReloadIcon } from 'assett/icons/reload.svg';
import { ReactComponent as SearchHideIcon } from 'assett/icons/search_hide_icon.svg';
import { ReactComponent as SearchIcon } from 'assett/icons/search_icon.svg';
import MultipleSelect from 'components/commons/form/MultipleSelect';
import SelectGeneric from 'components/commons/form/SelectGeneric';
import HeaderCellWithSorting from 'components/commons/list/HeaderCellWithSorting';
import {
	handleChange,
	resetInputFilters,
	setSearch,
} from 'feature/form/formSlice';
import { setDownloadAll } from 'feature/tableSelectedItems/tableSelectedItemsSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { Box, DatePicker, TableCell, TableHead, TableRow } from 'styles';
import IconButton from 'styles/button/IconButton';
import Checkbox from 'styles/checkbox/Checkbox';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';

const ClaimsHeader = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { t: tDbLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.DB_LABELS,
	});
	const { downloadAll } = useSelector(
		(store: RootState) => store.tableSelectedItems,
	);
	const {
		search,
		dateOfService,
		inputClaimStatus,
		claimClients,
		inputClient,
		claimsStatuses,
		inputClaimNetworkStatus,
		claimNetworkStatuses,
		claimReturnStatuses,
		inputClaimReturnStatus,
		inputHasComplaint,
		inputHasFraud,
		inputClaimMarkets,
		networks,
	} = useSelector((store: RootState) => store.form);

	const dispatch = useDispatch<AppDispatch>();

	const handleGenericSelect = (inputName: string, payload: string) => {
		dispatch(
			handleChange({
				name: inputName,
				value: payload !== 'All' ? payload : '',
			}),
		);
	};

	return (
		<TableHead>
			<TableRow>
				<TableCell sx={{ width: 44 }}>
					<Checkbox
						label=""
						onClick={() => {
							dispatch(setDownloadAll(!downloadAll));
						}}
						checked={downloadAll}
						sx={{ p: 0 }}
					/>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="externalClaimId"
						widthBox={220}
						widthInput={220}
						primarySorting="serviceDate"
					/>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="carrierClaimId"
						widthBox={220}
						widthInput={220}
					/>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="clientCodes"
						widthBox={230}
						widthInput={230}
						primarySorting="externalClaimId"
					>
						<SelectGeneric
							action={payload => handleGenericSelect('inputClient', payload)}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All'), id: 0 },
								...claimClients.map(el => {
									return {
										value: el.code,
										text: tDbLabels(el.name),
										id: el.code,
									};
								}),
							]}
							name={'clientName'}
							sx={{ width: 220 }}
							initialValue={inputClient || 'All'}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="firstName"
						widthBox={220}
						widthInput={220}
						primarySorting="externalClaimId"
					/>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="lastName"
						widthBox={220}
						widthInput={220}
						primarySorting="externalClaimId"
					/>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="serviceDate"
						widthBox={220}
						widthInput={220}
						primarySorting="externalClaimId"
					>
						<Box
							sx={{
								width: 220,
							}}
						>
							<DatePicker
								variant="field"
								label="Service date"
								value={dateOfService}
								onChange={value => {
									dispatch(
										handleChange({
											name: 'dateOfService',
											value: value,
										}),
									);
								}}
							/>
						</Box>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="claimStatus"
						widthBox={230}
						widthInput={230}
						primarySorting="externalClaimId"
					>
						<SelectGeneric
							action={(payload: string) =>
								handleGenericSelect('inputClaimStatus', payload)
							}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All'), id: 0 },
								...claimsStatuses.map(el => {
									return {
										value: el.name,
										text: tDbLabels(el.label),
										id: el.id,
									};
								}),
							]}
							name={'claim'}
							sx={{ width: 220 }}
							initialValue={inputClaimStatus || 'All'}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="memberLocation"
						widthBox={230}
						widthInput={230}
						primarySorting="externalClaimId"
					/>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="inNetwork"
						widthBox={230}
						widthInput={230}
						primarySorting="externalClaimId"
					>
						<SelectGeneric
							action={(payload: string) =>
								handleGenericSelect('inputClaimNetworkStatus', payload)
							}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All'), id: 0 },
								...claimNetworkStatuses.map(el => {
									return {
										value: el.name,
										text: tDbLabels(el.label),
										id: el.id,
									};
								}),
							]}
							name={'claimNetworkStatus'}
							sx={{ width: 220 }}
							initialValue={inputClaimNetworkStatus || 'All'}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="networkCodes"
						widthBox={230}
						widthInput={230}
						primarySorting="claimId"
					>
						<MultipleSelect
							items={networks.map(el => ({
								code: el.networkCode,
								name: el.name,
							}))}
							name={'claimMarket'}
							value={inputClaimMarkets}
							onChange={e =>
								dispatch(handleChange({ name: 'inputClaimMarkets', value: e }))
							}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="returnStatus"
						widthBox={230}
						widthInput={230}
						primarySorting="externalClaimId"
					>
						<SelectGeneric
							action={(payload: string) =>
								handleGenericSelect('inputClaimReturnStatus', payload)
							}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All'), id: 0 },
								...claimReturnStatuses.map(el => {
									return {
										value: el.code,
										text: tDbLabels(el.label),
										id: el.code,
									};
								}),
							]}
							name={'claimReturnStatus'}
							sx={{ width: 220 }}
							initialValue={inputClaimReturnStatus || 'All'}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="hasComplaint"
						widthBox={220}
						widthInput={220}
						primarySorting="externalClaimId"
					>
						<SelectGeneric
							action={(payload: string) =>
								handleGenericSelect('inputHasComplaint', payload)
							}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All'), id: 0 },
								{ value: 'true', text: tClientLabels('commons.yes'), id: 0 },
								{ value: 'false', text: tClientLabels('commons.no'), id: 0 },
							]}
							name={'hasComplaint'}
							sx={{ width: 220 }}
							initialValue={inputHasComplaint || 'All'}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ width: 220 }}>
					<HeaderCellWithSorting
						name="hasFraud"
						widthBox={220}
						widthInput={220}
						primarySorting="externalClaimId"
					>
						<SelectGeneric
							action={(payload: string) =>
								handleGenericSelect('inputHasFraud', payload)
							}
							items={[
								{ value: 'All', text: tClientLabels('forms.status.All'), id: 0 },
								{ value: 'true', text: tClientLabels('commons.yes'), id: 0 },
								{ value: 'false', text: tClientLabels('commons.no'), id: 0 },
							]}
							name={'hasFraud'}
							sx={{ width: 220 }}
							initialValue={inputHasFraud || 'All'}
						/>
					</HeaderCellWithSorting>
				</TableCell>
				<TableCell sx={{ minWidth: '56px', textAlign: 'center' }}>
					{search && (
						<IconButton onClick={() => dispatch(resetInputFilters())}>
							<ReloadIcon />
						</IconButton>
					)}
				</TableCell>
				<TableCell>
					<IconButton onClick={() => dispatch(setSearch(!search))}>
						{search ? <SearchHideIcon /> : <SearchIcon />}
					</IconButton>
				</TableCell>
			</TableRow>
		</TableHead>
	);
};

export default ClaimsHeader;
